import React, {FC, useCallback, useEffect, useState} from "react";
import ReactDOM from "react-dom";
import {Button, Popover} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import ColorPicker from "view/components/ColorPicker";
import {DEFAULT_OPACITY, toHex6} from "tools/theme/helpers";
import useStyles from "./styles";

export type CustomColorFormat = "color" | "background";

type Range = {index: number; length: number};

type CustomColorQuill = {
  format: (name: string, value: string, source: string) => void;
  // An array when the range holds several values.
  getFormat: (range: Range) => Record<string, string | string[] | undefined>;
  getSelection: (focus?: boolean) => Range | null;
  setSelection: (range: Range) => void;
  on: (event: string, handler: () => void) => void;
  off: (event: string, handler: () => void) => void;
};

type CustomColorControlProps = {
  toolbarId: string;
  format: CustomColorFormat;
  getQuill: () => CustomColorQuill | undefined;
};

// Shown when the selection has no colour or several colours.
const FALLBACK_HEX: Record<CustomColorFormat, string> = {
  color: "000000",
  background: "FFFFFF",
};

/**
 * Adds a "Custom colour" button under one of Quill's colour palettes and
 * opens the Primary Colour picker (HEX only, no opacity) in its place.
 */
const CustomColorControl: FC<CustomColorControlProps> = ({
  toolbarId,
  format,
  getQuill,
}) => {
  const classes = useStyles();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [range, setRange] = useState<Range | null>(null);
  const [color, setColor] = useState(`#${FALLBACK_HEX[format]}`);
  const [inputValue, setInputValue] = useState(color);

  // Quill builds the palette DOM itself (in ReactQuill's mount, before this
  // effect runs), so the button is portalled into it.
  useEffect(() => {
    const options = document
      .getElementById(toolbarId)
      ?.querySelector(`.ql-picker.ql-${format} .ql-picker-options`);
    if (!options) {
      return undefined;
    }
    const element = document.createElement("div");
    element.className = classes.container;
    options.appendChild(element);
    setContainer(element);
    return () => element.remove();
  }, [toolbarId, format, classes.container]);

  // Quill paints the bar under the toolbar icon only for palette colours and
  // clears it for any other value. Runs after Quill's own picker update (it
  // subscribes first), so a custom colour of the selection is painted back.
  // Quill also skips that update when a colour is set on a collapsed
  // selection (no event is emitted), hence the stale swatch is synced here.
  const paintSelectionColor = useCallback(() => {
    const quill = getQuill();
    const picker = container?.closest(".ql-picker");
    const label = picker?.querySelector<SVGElement>(".ql-color-label");
    if (!quill || !picker || !label) {
      return;
    }
    const selection = quill.getSelection();
    const hex = selection && toHex6(quill.getFormat(selection)[format]);
    if (!hex) {
      return;
    }
    picker
      .querySelectorAll<HTMLElement>(".ql-picker-item")
      .forEach((item) =>
        item.classList.toggle("ql-selected", toHex6(item.dataset.value) === hex)
      );
    // Same property Quill's ColorPicker paints.
    const property = label.tagName === "line" ? "stroke" : "fill";
    label.style[property] = `#${hex}`;
  }, [container, format, getQuill]);

  useEffect(() => {
    const quill = getQuill();
    if (!quill) {
      return undefined;
    }
    quill.on("editor-change", paintSelectionColor);
    return () => quill.off("editor-change", paintSelectionColor);
  }, [getQuill, paintSelectionColor]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    const quill = getQuill();
    if (!quill) {
      return;
    }
    // Same as a palette colour: focus restores the editor's last selection.
    const selection = quill.getSelection(true);
    const picker = event.currentTarget.closest<HTMLElement>(".ql-picker");
    picker?.classList.remove("ql-expanded");

    const hex = `#${
      (selection && toHex6(quill.getFormat(selection)[format])) ||
      FALLBACK_HEX[format]
    }`;
    setColor(hex);
    setInputValue(hex);
    setRange(selection);
    setAnchorEl(picker);
  };

  // `#` plus 6 HEX digits.
  const isComplete = inputValue.length === 7;

  const close = (hex?: string) => {
    const quill = getQuill();
    setAnchorEl(null);
    // Deferred past the popover's unmount: while it is open its focus trap
    // pulls focus back to it. `disableRestoreFocus` stops MUI from refocusing
    // the editor later, which would collapse the restored selection.
    setTimeout(() => {
      if (!quill || !range) {
        return;
      }
      quill.setSelection(range);
      if (hex) {
        quill.format(format, hex, "user");
        paintSelectionColor();
      }
    }, 0);
  };

  return (
    <>
      {container &&
        ReactDOM.createPortal(
          <button
            type="button"
            className={classes.button}
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleOpen}
          >
            <AddCircleOutlineIcon />
            Custom colour
          </button>,
          container
        )}
      {anchorEl && (
        <Popover
          open
          anchorEl={anchorEl}
          onClose={() => close()}
          disableRestoreFocus
          anchorOrigin={{vertical: "bottom", horizontal: "left"}}
          transformOrigin={{vertical: "top", horizontal: "left"}}
          PaperProps={{
            className: isComplete
              ? classes.paper
              : `${classes.paper} ${classes.incomplete}`,
          }}
          data-testid="custom-colour-popover"
        >
          <ColorPicker
            color={color}
            setColor={setColor}
            inputValue={inputValue}
            setInputValue={setInputValue}
            opacityValue={DEFAULT_OPACITY.toString()}
            updateColor={(hex) => setColor(hex)}
          />
          <Button
            color="primary"
            className={classes.submit}
            disabled={!isComplete}
            onClick={() => close(inputValue.toUpperCase())}
            data-testid="custom-colour-submit"
          >
            Submit
          </Button>
        </Popover>
      )}
    </>
  );
};

export default CustomColorControl;
